import { Tabs as AntTabs, type TabsProps as AntTabsProps } from 'antd'
import { cloneElement } from 'react'

export type TabsProps<T = string> = Omit<AntTabsProps, 'items' | 'activeKey' | 'onChange'> & {
  activeKey?: T
  onChange?: (tab: T) => void
  items?: ExtendWithAttributes<
    ({
      key: T
    } & Omit<NonNullable<AntTabsProps['items']>[number], 'key'>)[]
  >
}

type Attributes = { 'data-onboarding'?: string; 'data-test'?: string } & Record<string, string>

type ExtendWithAttributes<Items> = Items extends (infer Tab)[]
  ? (Tab & { attributes?: Attributes })[]
  : never

export const Tabs = <T extends string = string>(props: TabsProps<T>) => {
  const keyToAttributesMap =
    props.items?.reduce(
      (acc, item) => ({ ...acc, [item.key]: item.attributes ?? {} }),
      {} as Record<string, Attributes>
    ) ?? {}

  const omittedItems =
    props.items?.map(item =>
      Object.fromEntries(Object.entries(item).filter(([key, _]) => key !== 'attributes'))
    ) ?? []

  const omittedProps = { ...props, items: omittedItems } as typeof props

  return (
    <AntTabs
      size='small'
      {...omittedProps}
      onChange={props.onChange ? key => props.onChange?.(key as T) : undefined}
      renderTabBar={
        omittedProps.renderTabBar ??
        ((props, DefaultTabBar) => (
          <DefaultTabBar {...props}>
            {node => (node.key ? cloneElement(node, keyToAttributesMap[node.key]) : node)}
          </DefaultTabBar>
        ))
      }
    />
  )
}
